import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { translations } from './i18n';

const STORAGE_KEY = 'mh-lang';

const LanguageContext = createContext(null);

function getNested(obj, path) {
  let cur = obj;
  for (const key of path.split('.')) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = cur[key];
  }
  return cur;
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try {
      const s = localStorage.getItem(STORAGE_KEY);
      if (s === 'en' || s === 'so' || s === 'ar') return s;
    } catch {
      /* ignore */
    }
    return 'en';
  });

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, lang);
    } catch {
      /* ignore */
    }
    const html = document.documentElement;
    if (lang === 'so') {
      html.lang = 'so';
    } else if (lang === 'ar') {
      html.lang = 'ar';
    } else {
      html.lang = 'en';
    }
    html.dir = lang === 'ar' ? 'rtl' : 'ltr';
  }, [lang]);

  const setLang = useCallback((next) => {
    if (next === 'en' || next === 'so' || next === 'ar') {
      setLangState(next);
    }
  }, []);

  const t = useCallback(
    (key) => {
      const bundle = translations[lang];
      const v = getNested(bundle, key);
      if (typeof v === 'string') return v;
      return key;
    },
    [lang]
  );

  const value = useMemo(
    () => ({ lang, setLang, t }),
    [lang, setLang, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return ctx;
}
